import type { ApiEnvelope } from "@tanzeel/shared";

/**
 * Set at build time. The localhost fallback is deliberately dev-only: a
 * production bundle that quietly pointed at 127.0.0.1 would fail for every
 * user with no signal. vite.config.ts fails the build if it is missing.
 */
const configured = import.meta.env.VITE_API_BASE_URL ?? "";
export const API_BASE_URL =
  configured.length > 0
    ? configured
    : import.meta.env.DEV
      ? "http://127.0.0.1:8787"
      : "";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string | null,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

/** Unwraps `{ success, data, error, meta }`, throwing a typed error on failure. */
export async function unwrap<T>(response: Response): Promise<T> {
  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch (error) {
    throw new ApiError(
      response.status,
      "MALFORMED_RESPONSE",
      `Server returned a non-JSON response (${error instanceof Error ? error.message : String(error)})`,
      response.headers.get("X-Request-Id"),
    );
  }

  if (!response.ok || !envelope.success) {
    throw new ApiError(
      response.status,
      envelope.error?.code ?? "UNKNOWN",
      envelope.error?.message ?? "Request failed",
      envelope.meta?.requestId ?? response.headers.get("X-Request-Id"),
      envelope.error?.details,
    );
  }

  return envelope.data as T;
}
